import { useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { DayOfWeek } from '@weareopenr/types';
import { IconButton, MenuIcon, PriceBandIcon, Plus, Tile, TileVariant } from '@weareopenr/component-library';
import { ITimetableDay, ITimetableDaySchedule } from 'src/utils/timetables/groupSchedulesByDay';
import { toGridTemplateColumns, TimetableEntityType } from 'src/utils/timetables/helpers';
import { getDayLabel } from 'src/helpers/day/day';
import { TimetableView } from 'src/pages/Timetable/Timetable';
import styles from './TimetableDay.module.scss';

export interface TimetableDayProps {
  day: ITimetableDay;
  view: TimetableView;
  onAddSchedule: (entityType: TimetableEntityType, day: DayOfWeek) => void;
  // Whether the + button should be usable right now - false both when the user can't edit the
  // timetable at all, and transiently while the tray is open. Either way, the button fades out
  // and is removed from layout, letting the tiles claim the space it would otherwise reserve.
  isActionable: boolean;
  // Schedule ids to render with a dashed border, e.g. those affected by a pending add-schedule preview.
  changedScheduleIds?: Set<string>;
}

interface ITimetableLane {
  key: string;
  Icon: typeof MenuIcon;
  items: ITimetableDaySchedule[];
  variant: TileVariant;
  addLabel: string;
  entityType: TimetableEntityType;
  hidden: boolean;
}

// The button's fade duration - kept in sync with the CSS transition durations below, since
// CSSTransition needs to know it too, to time removing the node from the DOM until it's done.
const ADD_BUTTON_TRANSITION_MS = 150;

interface TimetableDayRowProps {
  Icon: typeof MenuIcon;
  items: ITimetableDaySchedule[];
  variant: TileVariant;
  addLabel: string;
  entityType: TimetableEntityType;
  day: DayOfWeek;
  isActionable: boolean;
  changedScheduleIds: Set<string>;
  onAddSchedule: (entityType: TimetableEntityType, day: DayOfWeek) => void;
}

const TimetableDayRow = ({
  Icon,
  items,
  variant,
  addLabel,
  entityType,
  day,
  isActionable,
  changedScheduleIds,
  onAddSchedule,
}: TimetableDayRowProps) => {
  const addButtonRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles['timetable-day-row']}>
      <div className={styles['timetable-day-icon']}>
        <Icon size="small" />
      </div>
      <div className={styles['timetable-day-tiles']} style={{ gridTemplateColumns: toGridTemplateColumns(items) }}>
        {items.map((item) => (
          <Tile
            key={item.scheduleId}
            mainText={item.name}
            secondaryText={`${item.startTime} - ${item.endTime}`}
            colour={item.colour}
            variant={variant}
            hasAccent
            label={item.isDefault ? 'Default' : undefined}
            border={changedScheduleIds.has(item.scheduleId) ? 'dashed' : 'solid'}
          />
        ))}
      </div>
      <CSSTransition
        in={isActionable}
        nodeRef={addButtonRef}
        timeout={ADD_BUTTON_TRANSITION_MS}
        classNames={{
          enter: styles['add-button-enter'],
          enterActive: styles['add-button-enter-active'],
          exit: styles['add-button-exit'],
          exitActive: styles['add-button-exit-active'],
        }}
        mountOnEnter
        unmountOnExit
      >
        <div ref={addButtonRef} className={styles['timetable-day-add-button']}>
          <IconButton
            icon={Plus}
            aria-label={addLabel}
            aria-hidden={!isActionable}
            tabIndex={isActionable ? 0 : -1}
            buttonStyle="outline"
            onClick={() => onAddSchedule(entityType, day)}
          />
        </div>
      </CSSTransition>
    </div>
  );
};

export const TimetableDay = ({
  day,
  view,
  onAddSchedule,
  isActionable,
  changedScheduleIds = new Set(),
}: TimetableDayProps) => {
  const lanes: ITimetableLane[] = [
    {
      key: 'menu',
      Icon: MenuIcon,
      items: day.menus,
      variant: 'muted',
      addLabel: 'Add menu schedule',
      entityType: 'menu',
      hidden: view === TimetableView.PRICING_ONLY,
    },
    {
      key: 'price',
      Icon: PriceBandIcon,
      items: day.priceSchemes,
      variant: 'bright',
      addLabel: 'Add price scheme schedule',
      entityType: 'priceScheme',
      hidden: view === TimetableView.MENUS_ONLY,
    },
  ];

  return (
    <div className={styles['timetable-day']}>
      <div className={styles['timetable-day-label']}>{getDayLabel(day.day)}</div>
      <div className={styles['timetable-day-rows']}>
        {lanes
          .filter((lane) => !lane.hidden)
          .map(({ key, Icon, items, variant, addLabel, entityType }) => (
            <TimetableDayRow
              key={key}
              Icon={Icon}
              items={items}
              variant={variant}
              addLabel={addLabel}
              entityType={entityType}
              day={day.day}
              isActionable={isActionable}
              changedScheduleIds={changedScheduleIds}
              onAddSchedule={onAddSchedule}
            />
          ))}
      </div>
    </div>
  );
};
